import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for fetching widget data from API
 * Handles loading, error states, and automatic refetching.
 *
 * IMPORTANT — AbortController cleanup:
 *   Each effect run creates its own AbortController and passes its signal to
 *   fetch(). The cleanup function returned by the effect aborts that controller
 *   when either:
 *     (a) the component unmounts (tab close, route change, page reload), OR
 *     (b) a dependency changes before the previous request has finished
 *         (e.g. filter change triggering a new requestJson while the old
 *          query is still executing on the backend).
 *
 *   Aborting on the frontend sends a TCP RST to the server so the .NET/MySQL
 *   stack can detect the client disconnect and close the DB connection
 *   immediately — preventing the "stacking queries / connection pool exhausted"
 *   problem that was observed when using axios without proper cancellation.
 *
 * @param {string} url          - API endpoint URL
 * @param {string} requestJson  - Primary request body (JSON string)
 * @param {string} requestJson1 - Secondary request data (filter JSON string)
 * @returns {{ data, loading, error, refetch }}
 */
export function useWidgetData(url, requestJson, requestJson1) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // refetchTrigger is incremented by refetch() to re-run the effect manually.
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    useEffect(() => {
        if (!url || !requestJson) {
            setLoading(false);
            return;
        }

        // Create a dedicated controller for this fetch attempt.
        // When the effect cleans up (deps change or unmount) we abort it,
        // which cancels the in-flight HTTP request and lets the server close
        // the DB connection instead of leaving it stuck in "executing" state.
        const controller = new AbortController();
        const { signal } = controller;

        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'requestHeader': requestJson1 || '',
                    },
                    body: requestJson,
                    signal,
                });

                const text = await response.text();

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const apiData = text ? JSON.parse(text) : [];
                setData(apiData);
                setLoading(false);
            } catch (err) {
                if (err.name === 'AbortError') {
                    // Intentional cancellation — not an error, don't touch state.
                    return;
                }
                console.error('Error fetching widget data:', err);
                setError(err.message || 'Failed to fetch data');
                setData(null);
                setLoading(false);
            }
        })();

        // Cleanup: abort the request when deps change or component unmounts.
        return () => {
            controller.abort();
        };
    }, [url, requestJson, requestJson1, refetchTrigger]);

    /** Imperatively re-run the fetch (used by widgets that have a manual refresh). */
    const refetch = useCallback(() => {
        setRefetchTrigger(prev => prev + 1);
    }, []);

    return { data, loading, error, refetch };
}

export default useWidgetData;
